/**
 * JSONL (JSON Lines) streaming writer and reader.
 *
 * Writer: Appends one JSON object per line to a file using a persistent fd.
 *         Used in worker processes to stream network requests, console logs,
 *         and API calls to disk instead of accumulating in memory.
 *
 * Reader: Reads a specific page of lines from a JSONL file without loading
 *         the entire file into memory.
 */

import { openSync, writeSync, closeSync, unlinkSync, mkdirSync, createReadStream } from 'node:fs';
import { join } from 'node:path';
import { tmpdir } from 'node:os';
import { randomUUID } from 'node:crypto';
import { createInterface } from 'node:readline';

const TESTRELIC_TMP_DIR = join(tmpdir(), 'testrelic-data');

/**
 * Append-only JSONL file writer.
 *
 * Keeps a file descriptor open for the lifetime of the writer.
 * Each `append()` call writes one JSON-serialized line synchronously,
 * so data hits disk immediately and is eligible for GC.
 */
export class JsonlFileWriter {
  private readonly filePath: string;
  private readonly fd: number;
  private count = 0;
  private closed = false;

  constructor(prefix: string) {
    mkdirSync(TESTRELIC_TMP_DIR, { recursive: true });
    this.filePath = join(TESTRELIC_TMP_DIR, `${prefix}-${randomUUID().substring(0, 8)}.jsonl`);
    this.fd = openSync(this.filePath, 'w');
  }

  /** Serialize `obj` as a single JSON line and write to disk. */
  append(obj: unknown): void {
    if (this.closed) return;
    const line = JSON.stringify(obj) + '\n';
    writeSync(this.fd, line);
    this.count++;
  }

  /** Absolute path to the JSONL file. */
  getPath(): string {
    return this.filePath;
  }

  /** Number of lines written so far. */
  getCount(): number {
    return this.count;
  }

  /** Close the file descriptor. Must be called before the file is moved. */
  close(): void {
    if (this.closed) return;
    this.closed = true;
    try {
      closeSync(this.fd);
    } catch {
      // Ignore close errors (fd may already be closed)
    }
  }

  /** Remove the temporary file from disk. */
  cleanup(): void {
    try {
      unlinkSync(this.filePath);
    } catch {
      // File may already be moved/deleted
    }
  }
}

/**
 * Read a page of items from a JSONL file.
 *
 * Uses `readline` to stream through the file line by line.
 * Only parses lines within the requested page range.
 * Skips lines before the page start without parsing them.
 *
 * @param filePath    — Absolute path to the JSONL file
 * @param page        — 1-based page number
 * @param pageSize    — Number of items per page
 * @param knownTotal  — If provided, avoids counting remaining lines after the page
 */
export async function readJsonlPage<T>(
  filePath: string,
  page: number,
  pageSize: number,
  knownTotal?: number,
): Promise<{ items: T[]; total: number; page: number; pageSize: number; totalPages: number }> {
  const skip = (page - 1) * pageSize;
  const items: T[] = [];
  let lineCount = 0;

  const rl = createInterface({
    input: createReadStream(filePath, { encoding: 'utf-8' }),
    crlfDelay: Infinity,
  });

  for await (const line of rl) {
    if (line.length === 0) continue;
    if (lineCount >= skip && items.length < pageSize) {
      try {
        items.push(JSON.parse(line) as T);
      } catch {
        // Skip malformed lines
      }
    }
    lineCount++;
    // Early exit if we have all items and know the total
    if (items.length >= pageSize && knownTotal !== undefined) {
      break;
    }
  }

  const total = knownTotal ?? lineCount;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return { items, total, page, pageSize, totalPages };
}
